"use client";

import { UserAvatar } from "@cmp-src/data-display/UserAvatar/UserAvatar";
import { Visit, VisitStatus } from "@gettactic/api";
import { AgendaCardLayout } from "@gettactic/components/src/components/reservation-card/AgendaCardLayout";
import { cn } from "@gettactic/helpers";
import { format } from "date-fns";
import { UserRound } from "lucide-react";
import { useMemo } from "react";

const statusConfig: Record<
  VisitStatus,
  { label: string; dotClass: string; textClass: string }
> = {
  expected: {
    label: "Expected",
    dotClass: "bg-amber-400",
    textClass: "text-amber-600"
  },
  checked_in: {
    label: "Checked In",
    dotClass: "bg-green-500",
    textClass: "text-green-600"
  },
  checked_out: {
    label: "Checked Out",
    dotClass: "bg-neutral-400",
    textClass: "text-neutral-500"
  }
};

interface VisitCardProps {
  visit: Visit;
  onCardClick?: () => void;
  isSelected?: boolean;
}

export const VisitCard: React.FC<VisitCardProps> = ({
  visit,
  onCardClick,
  isSelected
}) => {
  const expectedTime = useMemo(() => {
    if (!visit.expected) return null;
    return format(new Date(visit.expected), "h:mm a");
  }, [visit.expected]);

  const checkedInTime = useMemo(() => {
    if (!visit.checked_in) return null;
    return format(new Date(visit.checked_in), "h:mm a");
  }, [visit.checked_in]);

  const status = statusConfig[visit.status] ?? statusConfig.expected;

  return (
    <AgendaCardLayout
      topLeft={
        <div className="flex items-center gap-2">
          <UserRound className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-sm">
            {visit.status === "checked_in" && checkedInTime
              ? checkedInTime
              : (expectedTime ?? "No time")}
          </span>
          <span
            className={cn(
              "flex items-center gap-1 rounded-full px-2 py-0.5 font-medium text-xs",
              status.textClass
            )}
          >
            <span
              className={cn(
                "inline-block h-1.5 w-1.5 rounded-full",
                status.dotClass
              )}
            />
            {status.label}
          </span>
        </div>
      }
      bottomLeft={
        <div className="flex items-center gap-2.5">
          <UserAvatar
            user={{
              name: visit.visitor.name || "Visitor",
              profile_photo_url: visit.guest_photo_url || null
            }}
            size="sm"
            isPlaneImage
          />
          <div className="flex flex-col space-y-0.5">
            <span className="font-medium text-neutral-900 text-sm">
              {visit.visitor.name || "Unknown Visitor"}
            </span>
            {visit.visitor_type && (
              <span className="text-neutral-500 text-xs">
                {visit.visitor_type.name}
              </span>
            )}
          </div>
        </div>
      }
      onCardClick={onCardClick}
      isSelected={isSelected}
      hasHover={!!onCardClick}
      innerClassName="border-neutral-200"
    />
  );
};
